import type { ReactNode } from 'react';
import { NavLink, useMatch } from 'react-router-dom';

import { cn } from '../../../lib/utils';
import { Badge } from '../../ui/Badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../ui/Tooltip';
import { ACCENT_CLASS, BADGE_LABEL } from './constants';
import type { SidebarAccent, SidebarBadge } from './index';

type Props = {
  to?: string;
  label: string;
  icon?: ReactNode;
  collapsed: boolean;
  // NavLink matches descendant paths by default; the root entry ("/") must use
  // `end` so it isn't perpetually active on every nested route.
  end?: boolean;
  // Glyph tint; dropped for the dark on-accent color when the row is selected.
  accent?: SidebarAccent;
  // Trailing status pill; hidden on the collapsed rail (icon-only).
  badge?: SidebarBadge;
  // Renders the row as greyed, non-interactive text (a page not available yet).
  disabled?: boolean;
  // Drawer variant passes this to dismiss the off-canvas overlay on navigation.
  onNavigate?: () => void;
};

const ROW_BASE = 'flex h-8 items-center gap-2 rounded-md text-sm transition-colors';

function rowLayout(collapsed: boolean): string {
  return collapsed ? 'w-8 justify-center px-0' : 'w-full pl-2 pr-3';
}

function IconSlot({ icon, className }: { icon?: ReactNode; className?: string }) {
  if (!icon) return null;
  return (
    <span
      aria-hidden="true"
      className={cn('flex h-4 w-4 shrink-0 items-center justify-center [&_svg]:size-4', className)}
    >
      {icon}
    </span>
  );
}

// Wraps collapsed (icon-only) rows so the label still surfaces on hover.
function withTooltip(node: ReactNode, label: string, collapsed: boolean): ReactNode {
  if (!collapsed) return node;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{node}</TooltipTrigger>
      <TooltipContent side="right">{label}</TooltipContent>
    </Tooltip>
  );
}

function ActiveItem({
  to,
  label,
  icon,
  collapsed,
  end,
  accent,
  badge,
  onNavigate,
}: Props & { to: string }) {
  // Resolve active state ourselves instead of via NavLink's function-className:
  // when collapsed, the link is wrapped in Radix `<TooltipTrigger asChild>`, and
  // Slot stringifies a function className (joins it as text), dropping the active
  // styling. A precomputed string className survives the Slot merge.
  const isActive = useMatch({ path: to, end: end ?? to === '/' }) != null;
  const iconColor = isActive ? undefined : accent ? ACCENT_CLASS[accent] : undefined;

  const link = (
    <NavLink
      to={to}
      end={end}
      data-testid="nav-item-label"
      aria-label={collapsed ? label : undefined}
      onClick={onNavigate}
      className={cn(
        ROW_BASE,
        rowLayout(collapsed),
        isActive
          ? 'bg-sidebar-accent text-sidebar-accent-foreground hover:bg-sidebar-accent-pressed'
          : 'text-sidebar-foreground hover:bg-sidebar-neutral-hover',
      )}
    >
      <IconSlot icon={icon} className={iconColor} />
      {!collapsed && <span className="flex-1 truncate text-left">{label}</span>}
      {!collapsed && badge && (
        <Badge variant={badge} className="ml-1 shrink-0">
          {BADGE_LABEL[badge]}
        </Badge>
      )}
    </NavLink>
  );

  return withTooltip(link, label, collapsed);
}

function DisabledItem({ label, icon, collapsed }: Pick<Props, 'label' | 'icon' | 'collapsed'>) {
  const row = (
    <span
      aria-disabled="true"
      data-testid="nav-item-disabled"
      className={cn(
        ROW_BASE,
        rowLayout(collapsed),
        'cursor-default text-[var(--color-sidebar-item-disabled)]',
      )}
    >
      <IconSlot icon={icon} />
      {!collapsed && <span className="flex-1 truncate text-left">{label}</span>}
    </span>
  );

  return withTooltip(row, label, collapsed);
}

export function SidebarItem(props: Props) {
  if (props.disabled || !props.to) {
    return <DisabledItem label={props.label} icon={props.icon} collapsed={props.collapsed} />;
  }
  return <ActiveItem {...props} to={props.to} />;
}
